import { DOCUMENT, Location } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, first, map, shareReplay } from 'rxjs/operators';
import { Scheme } from '../../../../@fuse/services/config';
import { AuthService } from '../../../core/auth/auth.service';
import { AuthUtils } from '../../../core/auth/auth.utils';
import { ShopsService } from '../../backoffice/shops/shops.service';
import { ExternalModuleConfig } from '../external-module.types';

@Component({
    selector: 'app-external-module-shell',
    imports: [],
    templateUrl: './external-module-shell.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './external-module-shell.component.scss',
})
export class ExternalModuleShellComponent implements OnDestroy {
    @ViewChild('externalFrame') frameRef!: ElementRef<HTMLIFrameElement>;
    private sanitizer = inject(DomSanitizer);
    private document = inject(DOCUMENT);
    private route = inject(ActivatedRoute);
    private destroyRef = inject(DestroyRef);
    private shopsService = inject(ShopsService);
    private themeObserver?: MutationObserver;
    private externalOrigin!: string;
    private shopCodes$!: Observable<string[]>;
    private router = inject(Router);
    private location = inject(Location);

    safeUrl!: SafeResourceUrl;

    constructor(private auth: AuthService) {}

    ngOnInit() {
        this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            const config: ExternalModuleConfig = data['moduleConfig'];
            this.externalOrigin = new URL(config.url).origin;

            const deepLink = this.route.snapshot.queryParamMap.get('deepLink');
            const finalUrl = deepLink ? new URL(deepLink, config.url).toString() : config.url;

            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);
        });

        this.shopCodes$ = this.shopsService.getLookup(10000, 0).pipe(
            map((r) => r.result?.items?.map((s) => s.code) ?? []),
            shareReplay(1)
        );
    }

    ngAfterViewInit() {
        window.addEventListener('message', this.onMessageFromIframe);
        this.themeObserver = new MutationObserver(() => {
            this.sendTheme(this.getCurrentScheme());
        });
        this.themeObserver.observe(this.document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener('message', this.onMessageFromIframe);
        this.themeObserver?.disconnect();
    }

    onIframeLoad() {
        setTimeout(() => {
            forkJoin({
                token: this.getValidToken$(),
                shops: this.shopCodes$.pipe(first()),
            })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(({ token, shops }) => {
                    this.frameRef.nativeElement.contentWindow?.postMessage(
                        {
                            type: 'TB_INIT',
                            token,
                            theme: this.getCurrentScheme(),
                            shops,
                        },
                        this.externalOrigin
                    );
                });
        }, 300);
    }

    private onMessageFromIframe = (event: MessageEvent) => {
        if (event.origin !== this.externalOrigin) return;

        // New auth token was requested
        if (event.data?.type === 'TB_TOKEN_REQUEST') {
            this.sendToken();
            return;
        }

        // Module changed page: manually update url
        if (event.data?.type === 'TB_ROUTE_CHANGED' && typeof event.data.path === 'string') {
            const currentBase = this.router.url.split('?')[0]; // eg. /modules/booking
            const shellBase = currentBase.split('/').slice(0, 3).join('/');
            const newUrl = `${shellBase}?deepLink=${encodeURIComponent(event.data.path)}`;
            this.location.replaceState(newUrl);
            return;
        }
    };

    private sendToken() {
        this.getValidToken$()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((token) => this.postToken(token));
    }

    private getValidToken$(): Observable<string | null> {
        const current = this.auth.accessToken;
        // Try to be proactive: send a valid token that will not expire soon.
        if (current && !AuthUtils.isTokenExpired(current, 60)) {
            return of(current);
        }
        return this.auth.refreshToken().pipe(
            map(() => this.auth.accessToken),
            catchError(() => of(this.auth.accessToken)) // best effort: send whatever we have
        );
    }

    private postToken(token: string | null) {
        this.frameRef.nativeElement.contentWindow?.postMessage({ type: 'AUTH_TOKEN', token }, this.externalOrigin);
    }

    private sendTheme(theme: Scheme) {
        console.log('Sending theme');
        this.frameRef.nativeElement.contentWindow?.postMessage(
            { type: 'TB_THEME_CHANGED', theme },
            this.externalOrigin
        );
    }

    private getCurrentScheme(): Scheme {
        return this.document.body.classList.contains('dark') ? 'dark' : 'light';
    }
}
